import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Home,
  Image as ImageIcon,
  Loader2,
  ScanSearch,
  ShoppingCart,
} from "lucide-react";
import { fetchProduct, createShopifyCart } from "@/lib/api";
import SectionFadeIn from "@/components/SectionFadeIn";
import { MuralConfigurator } from "@/components/murals/MuralConfigurator";
import { getRouteBackTarget, getRouteState } from "@/lib/navigation";
import ImageLightbox from "@/components/ImageLightbox";

const SHOPIFY_DOMAIN = process.env.REACT_APP_SHOPIFY_STORE_DOMAIN || "srbt1r-qn.myshopify.com";

const DETAIL_POINTS = [
  "Scaled to your exact wall size before print.",
  "Premium wallpaper and vinyl finishes available.",
  "Quote turnaround within 24 hours once measurements arrive.",
];

function stripHtml(html) {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, " ").replace(/&[^;]+;/g, " ").replace(/\s+/g, " ").trim();
}

function formatRate(amount) {
  const numeric = parseFloat(amount || "14");
  if (Number.isNaN(numeric)) return "From \u00a314/m\u00b2";
  return `From \u00a3${numeric.toFixed(2)}/m\u00b2`;
}

export default function WallMuralProductDetail() {
  const { handle } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [previewMode, setPreviewMode] = useState("design");
  const [muralConfig, setMuralConfig] = useState({ widthM: 0, heightM: 0, areaSqm: 0, materialLabel: "", estimatedPrice: 0, dimensionsText: "" });
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [gallerySlots, setGallerySlots] = useState([]);

  useEffect(() => {
    let active = true;
    fetchProduct(handle)
      .then((response) => { if (active) setProduct(response.product); })
      .catch(() => { if (active) setProduct(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // `fetchProduct` is a stable module import; `setProduct`/`setLoading` are
    // stable useState setters; `active`/`response` are locals. Only `handle` triggers refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handle]);

  // Fetch presentation gallery slots for this product (if configured)
  useEffect(() => {
    if (!product?.id) return;
    let active = true;
    fetch(`/api/products/gallery/${product.id}`)
      .then((r) => r.json())
      .then((data) => { if (active && data.gallery?.length) setGallerySlots(data.gallery); })
      .catch((err) => { console.error("[WallMuralProductDetail] Gallery fetch failed:", err); });
    return () => { active = false; };
    // All non-listed names are module-level (fetch), stable setters, or locals.
    // Only `product?.id` genuinely drives re-fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  // Use gallery slots when available, fall back to Shopify images
  const rawImages = gallerySlots.length > 0
    ? gallerySlots.map((s) => ({ url: s.asset_url, altText: s.label, slotKey: s.slot_key }))
    : (product?.images || []);

  const images = rawImages;
  const currentImage = images[activeImage] || images[0] || null;
  const description = stripHtml(product?.description);
  const priceLabel = formatRate(product?.priceRange?.minVariantPrice?.amount);
  const tags = useMemo(
    () => (product?.tags || []).filter(Boolean).slice(0, 5),
    [product?.tags]
  );
  const shopUrl = product ? `https://${SHOPIFY_DOMAIN}/products/${product.handle}` : "#";
  const baseRate = parseFloat(product?.priceRange?.minVariantPrice?.amount || "18") || 18;
  // The single default variant for this mural — used for Shopify checkout
  const muralVariant = product?.variants?.[0];
  const hasVariant = !!(muralVariant?.id);

  const handleMuralCheckout = async () => {
    if (!hasVariant || checkoutLoading) return;
    setCheckoutLoading(true);
    const note = [
      `Mural: ${product.title}`,
      muralConfig.dimensionsText ? `Dimensions: ${muralConfig.dimensionsText}` : null,
      muralConfig.areaSqm > 0 ? `Area: ${muralConfig.areaSqm.toFixed(2)} m²` : null,
      muralConfig.materialLabel ? `Material: ${muralConfig.materialLabel}` : null,
      muralConfig.estimatedPrice > 0 ? `Estimate: £${muralConfig.estimatedPrice.toFixed(2)}` : null,
    ].filter(Boolean).join(" | ");
    try {
      const result = await createShopifyCart({ variantId: muralVariant.id, quantity: 1, handle: product.handle, note });
      window.open(result.checkout_url || shopUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("[WallMuralProductDetail] cart creation failed, using direct URL:", err);
      window.open(shopUrl, "_blank", "noopener,noreferrer");
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="py-16 md:py-24 max-w-7xl mx-auto px-6 md:px-12" data-testid="mural-detail-loading">
        <div className="animate-pulse grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-10 md:gap-14">
          <div className="aspect-[16/10] rounded-sm bg-brand-surface" />
          <div className="space-y-4 py-4">
            <div className="h-4 w-28 rounded bg-brand-surface" />
            <div className="h-10 w-3/4 rounded bg-brand-surface" />
            <div className="h-6 w-1/3 rounded bg-brand-surface" />
            <div className="h-36 rounded bg-brand-surface" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="py-28 max-w-lg mx-auto px-6 text-center" data-testid="mural-detail-not-found">
        <h1 className="font-heading text-3xl text-brand-text">Mural Not Found</h1>
        <p className="mt-3 text-[14px] text-brand-text-secondary leading-relaxed">
          This mural may have been removed or the link is no longer active.
        </p>
        <Link
          to={getRouteBackTarget(location, "/")}
          className="mt-6 inline-flex items-center gap-2 text-[13px] font-medium text-brand-gold"
          data-testid="mural-detail-not-found-back"
        >
          <ArrowLeft size={14} /> Back to Wall Murals
        </Link>
      </div>
    );
  }

  return (
    <div className="py-16 md:py-24" data-testid={`mural-product-detail-${handle}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionFadeIn>
          <button
            type="button"
            onClick={() => navigate(getRouteBackTarget(location, "/"))}
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-brand-text-secondary/55 transition-colors duration-300 hover:text-brand-gold"
            data-testid="mural-detail-back-link"
          >
            <ArrowLeft size={12} /> Back
          </button>
        </SectionFadeIn>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1.08fr_0.92fr] gap-10 md:gap-14 items-start">
          <SectionFadeIn>
            <div>
              <button
                type="button"
                onClick={() => currentImage && setLightboxIndex(activeImage)}
                className="relative block w-full overflow-hidden rounded-sm border border-brand-border bg-brand-bg cursor-zoom-in"
                data-testid="mural-detail-preview"
              >
                <div className="aspect-[16/10]">
                  {currentImage ? (
                    previewMode === "room" ? (
                      <div
                        className="relative h-full w-full overflow-hidden bg-[radial-gradient(circle_at_50%_10%,rgba(255,255,255,0.08),transparent_34%),linear-gradient(180deg,#151b24_0%,#0e1219_58%,#090b11_100%)]"
                        data-testid="mural-room-preview-bg"
                      >
                        <div
                          className="absolute left-[7%] right-[7%] top-[7%] bottom-[28%] overflow-hidden"
                          style={{
                            clipPath: "polygon(4% 0%, 96% 0%, 100% 100%, 0% 100%)",
                            transform: "perspective(1400px) rotateX(2deg)",
                            transformOrigin: "center top",
                            willChange: "transform",
                          }}
                          data-testid="mural-room-wall-plane"
                        >
                          <img
                            src={currentImage.url}
                            alt={currentImage.altText || product.title}
                            className="h-full w-full object-cover"
                            data-testid="mural-room-preview-image"
                          />
                          <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(8,10,14,0.28)_0%,rgba(255,255,255,0.03)_22%,rgba(255,255,255,0.01)_72%,rgba(8,10,14,0.42)_100%)] mix-blend-multiply" />
                          <div className="absolute inset-0 shadow-[inset_0_30px_60px_rgba(255,255,255,0.08),inset_0_-40px_55px_rgba(0,0,0,0.32)]" />
                        </div>
                        <div
                          className="absolute left-0 top-[2%] bottom-[28%] w-[11%] bg-[linear-gradient(90deg,rgba(8,10,14,0.96),rgba(8,10,14,0.1))]"
                          style={{ clipPath: "polygon(0 0, 100% 8%, 100% 100%, 0 100%)" }}
                        />
                        <div
                          className="absolute right-0 top-[2%] bottom-[28%] w-[11%] bg-[linear-gradient(270deg,rgba(8,10,14,0.98),rgba(8,10,14,0.08))]"
                          style={{ clipPath: "polygon(0 8%, 100% 0, 100% 100%, 0 100%)" }}
                        />
                        <div
                          className="absolute inset-x-0 bottom-0 h-[31%] bg-[linear-gradient(180deg,rgba(15,19,26,0.94)_0%,rgba(7,9,12,1)_100%)]"
                          style={{ clipPath: "polygon(17% 0%, 83% 0%, 100% 100%, 0% 100%)" }}
                        />
                        <div className="absolute inset-x-[15%] bottom-[28.5%] h-[2px] bg-white/10 blur-[1px]" />
                        <div className="absolute left-[14%] bottom-[11%] h-[12%] w-[34%] rounded-t-[24px] rounded-b-[10px] bg-black/34 shadow-[0_-12px_35px_rgba(0,0,0,0.18)]" />
                        <div className="absolute left-[18%] bottom-[21%] h-[5%] w-[26%] rounded-full bg-black/20 blur-2xl" />
                        <div className="absolute right-[16%] bottom-[11%] h-[10%] w-[16%] rounded-[22px] bg-black/26 shadow-[0_-10px_28px_rgba(0,0,0,0.16)]" />
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_45%,rgba(10,13,20,0.16)_100%)]" />
                      </div>
                    ) : (
                      <div className="relative h-full w-full overflow-hidden bg-[radial-gradient(circle_at_top,_rgba(203,160,82,0.10),_transparent_32%),linear-gradient(145deg,_rgba(255,255,255,0.02),_rgba(10,13,20,0.9))]">
                        <img
                          src={currentImage.url}
                          alt={currentImage.altText || product.title}
                          className="h-full w-full object-cover"
                          data-testid="mural-flat-preview-image"
                        />
                        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),transparent_24%,rgba(10,13,20,0.14)_100%)]" />
                      </div>
                    )
                  ) : (
                    <div className="flex h-full w-full items-end bg-gradient-to-br from-brand-surface to-brand-bg p-8">
                      <p className="max-w-sm text-[14px] leading-relaxed text-brand-text-secondary/45" data-testid="mural-detail-fallback-copy">
                        {description || "Your mural artwork will appear here once imagery is available in Shopify."}
                      </p>
                    </div>
                  )}
                </div>
              </button>

              {currentImage && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="mural-preview-mode-switcher">
                  {[
                    { id: "design", label: "Full Design", icon: ImageIcon },
                    { id: "room", label: "Preview", icon: Home },
                  ].map((mode) => (
                    <button
                      key={mode.id}
                      type="button"
                      onClick={() => setPreviewMode(mode.id)}
                      className={`inline-flex items-center gap-2 rounded-sm border px-4 py-2.5 text-[12px] font-medium transition-all duration-200 ${
                        previewMode === mode.id
                          ? "border-brand-gold bg-brand-gold/5 text-brand-gold"
                          : "border-brand-border text-brand-text-secondary hover:border-brand-text-secondary/40"
                      }`}
                      data-testid={`mural-preview-mode-${mode.id}`}
                    >
                      <mode.icon size={14} /> {mode.label}
                    </button>
                  ))}
                </div>
              )}

              {images.length > 1 && (
                <div className="mt-4 flex flex-wrap gap-2" data-testid="mural-thumbnails">
                  {images.map((image, index) => (
                    <button
                      key={`${image.url}-${index}`}
                      type="button"
                      onClick={() => setActiveImage(index)}
                      className={`h-16 w-20 overflow-hidden rounded-sm border transition-colors duration-200 ${
                        activeImage === index
                          ? "border-brand-gold"
                          : "border-brand-border hover:border-brand-gold/40"
                      }`}
                      data-testid={`mural-thumbnail-${index}`}
                    >
                      <img src={image.url} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </SectionFadeIn>

          <SectionFadeIn delay={80}>
            <div>
              <p className="text-[11px] uppercase tracking-[0.24em] font-semibold text-brand-gold" data-testid="mural-detail-type">
                {product.productType || "Wall Mural"}
              </p>
              <h1
                className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight leading-[0.98] text-brand-text"
                data-testid="mural-detail-title"
              >
                {product.title}
              </h1>
              <p className="mt-4 text-[24px] font-medium text-brand-text" data-testid="mural-detail-price">
                {priceLabel}
              </p>
              <p className="mt-4 max-w-xl text-[14px] leading-relaxed text-brand-text-secondary" data-testid="mural-detail-description">
                {description || "Custom printed to your wall size with premium finish options and a same-day quote response."}
              </p>

              {tags.length > 0 && (
                <div className="mt-5 flex flex-wrap gap-2" data-testid="mural-detail-tags">
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full border border-brand-border px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] text-brand-text-secondary/65"
                      data-testid={`mural-tag-${tag.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="mural-detail-highlights">
                {[
                  { label: "Made to fit", value: "Any wall size" },
                  { label: "Artwork prep", value: "Scaled before print" },
                  { label: "Lead time", value: "Quote in 24h" },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="rounded-sm border border-brand-border bg-brand-surface/30 px-4 py-4"
                    data-testid={`mural-highlight-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    <p className="text-[10px] uppercase tracking-[0.18em] text-brand-text-secondary/45">
                      {item.label}
                    </p>
                    <p className="mt-2 text-[14px] font-medium text-brand-text">{item.value}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <MuralConfigurator
                  productTitle={product.title}
                  baseRate={baseRate}
                  ctaLabel="Start My Quote"
                  testIdPrefix="mural-detail-config"
                  onConfigChange={setMuralConfig}
                />
              </div>

              <div className="mt-4 flex flex-col sm:flex-row gap-3" data-testid="mural-detail-ctas">
                {/* Secondary: Order via Shopify — only show when product has a valid Shopify variant */}
                {hasVariant && (
                  <button
                    type="button"
                    onClick={handleMuralCheckout}
                    disabled={checkoutLoading}
                    className="inline-flex items-center justify-center gap-2 rounded-sm bg-brand-gold px-6 py-3.5 text-[13px] font-semibold tracking-wide text-brand-bg transition-colors duration-300 hover:bg-brand-gold-hover disabled:opacity-60"
                    data-testid="mural-detail-shopify-cta"
                  >
                    {checkoutLoading ? (
                      <><Loader2 size={14} className="animate-spin" /> Preparing...</>
                    ) : (
                      <><ShoppingCart size={14} /> Order via Shopify</>
                    )}
                  </button>
                )}
                <Link
                  to="/request-quote?type=Wall+Mural"
                  state={getRouteState(location)}
                  className="inline-flex items-center justify-center gap-2 rounded-sm border border-brand-border px-6 py-3.5 text-[13px] font-medium tracking-wide text-brand-text-secondary transition-colors duration-300 hover:border-brand-text-secondary/45 hover:text-brand-text"
                  data-testid="mural-detail-general-quote-cta"
                >
                  Need Bespoke Artwork? <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </SectionFadeIn>
        </div>

        <SectionFadeIn>
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-6" data-testid="mural-detail-support-section">
            <div className="rounded-sm border border-brand-border bg-brand-surface/25 p-6 md:p-7">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-brand-gold">
                <ScanSearch size={14} /> Before We Print
              </div>
              <div className="mt-5 space-y-4">
                {DETAIL_POINTS.map((point, index) => (
                  <div key={point} className="flex items-start gap-3" data-testid={`mural-detail-point-${index + 1}`}>
                    <span className="mt-0.5 rounded-full border border-brand-gold/30 p-1 text-brand-gold">
                      <Check size={12} />
                    </span>
                    <p className="text-[14px] leading-relaxed text-brand-text-secondary">{point}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-sm border border-brand-border bg-[linear-gradient(135deg,rgba(203,160,82,0.10),rgba(10,13,20,0.96))] p-6 md:p-7" data-testid="mural-detail-custom-art-card">
              <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-brand-gold">
                Custom Project Support
              </p>
              <h2 className="mt-3 font-heading text-3xl tracking-tight text-brand-text">
                Want This Look With Your Own Artwork?
              </h2>
              <p className="mt-3 max-w-2xl text-[14px] leading-relaxed text-brand-text-secondary">
                We can adapt your own photography, branded artwork, or sourced imagery into a mural-ready layout.
                Send us the wall size and any inspiration, and we’ll guide the best finish for the space.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/request-quote?type=Wall+Mural"
                  state={getRouteState(location)}
                  className="inline-flex items-center justify-center gap-2 rounded-sm bg-brand-gold px-6 py-3.5 text-[13px] font-medium tracking-wide text-brand-bg transition-colors duration-300 hover:bg-brand-gold-hover"
                  data-testid="mural-detail-custom-quote-cta"
                >
                  Request Custom Mural Quote <ArrowRight size={14} />
                </Link>
                <Link
                  to="/collections/wall-murals"
                  state={getRouteState(location)}
                  className="inline-flex items-center justify-center gap-2 rounded-sm border border-brand-border px-6 py-3.5 text-[13px] font-medium tracking-wide text-brand-text-secondary transition-colors duration-300 hover:border-brand-text-secondary/40 hover:text-brand-text"
                  data-testid="mural-detail-browse-more-cta"
                >
                  Browse More Murals <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </SectionFadeIn>
      </div>

      {lightboxIndex !== null && (
        <ImageLightbox
          images={images}
          activeIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={() => setLightboxIndex((current) => (current - 1 + images.length) % images.length)}
          onNext={() => setLightboxIndex((current) => (current + 1) % images.length)}
          title={product.title}
          testIdPrefix="mural-lightbox"
        />
      )}
    </div>
  );
}
